/**
 * @fileoverview Firebase User Data Service
 *
 * Handles server-side storage of critical user data (MMR, games, achievements)
 * for both anonymous and authenticated users
 *
 * CLEANUP POLICY:
 * Anonymous accounts with lastActive > 1 year should be deleted via server-side
 * Cloud Function or scheduled task to prevent database bloat.
 * Client-side cleanup happens in AuthContext when user returns after 1 year.
 */

import { doc, setDoc, getDoc, updateDoc, increment, arrayUnion } from 'firebase/firestore';
import { db } from './firebaseConfig';
import { UserProfile, MMRData } from '../MMR/types';
import { containsInappropriateContent } from '../../utils/profanityFilter';

export interface FirebaseUserData {
  uid: string;
  profile: UserProfile;
  gamesPlayed: number;
  createdAt: Date;
  lastActive: Date;
  accountType: 'anonymous' | 'authenticated';
  deviceId?: string; // For anonymous accounts
}

export class UserDataService {
  private static readonly USERS_COLLECTION = 'users';

  /** Generate a random 3-digit friend tag (000-999) */
  static generateFriendTag(): string {
    return String(Math.floor(Math.random() * 1000)).padStart(3, '0');
  }

  /**
   * Create initial user data in Firebase for anonymous users
   */
  static async createAnonymousUserData(
    uid: string,
    profile: UserProfile,
    deviceId: string
  ): Promise<void> {
    const userData: FirebaseUserData = {
      uid,
      profile,
      gamesPlayed: 0,
      createdAt: new Date(),
      lastActive: new Date(),
      accountType: 'anonymous',
      deviceId
    };

    const userRef = doc(db, this.USERS_COLLECTION, uid);
    await setDoc(userRef, userData);

  }

  /**
   * Get user data from Firebase
   */
  static async getUserData(uid: string): Promise<FirebaseUserData | null> {
    try {
      const userRef = doc(db, this.USERS_COLLECTION, uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const data = userSnap.data() as FirebaseUserData;

        // Helper function to safely convert to Date
        const toDate = (value: any): Date | undefined => {
          if (!value) return undefined;
          if (value instanceof Date) return value;
          try {
            const date = new Date(value);
            // Check if the date is valid
            if (isNaN(date.getTime())) return undefined;
            return date;
          } catch {
            return undefined;
          }
        };

        // Convert Firestore timestamps back to Date objects
        data.createdAt = data.createdAt instanceof Date ? data.createdAt : new Date(data.createdAt);
        data.lastActive = data.lastActive instanceof Date ? data.lastActive : new Date(data.lastActive);
        data.profile.lastActive = data.profile.lastActive instanceof Date ? data.profile.lastActive : new Date(data.profile.lastActive);

        // Handle mmr.createdAt - if invalid, use data.createdAt or current date
        const mmrCreatedAt = toDate(data.profile.mmr.createdAt);
        if (!mmrCreatedAt) {
          // Fix missing/invalid createdAt - use account createdAt or current date
          data.profile.mmr.createdAt = data.createdAt || new Date();

          // Save the fix back to Firebase (fire and forget)
          this.updateUserProfile(userSnap.id, data.profile).catch(() => {
            // Silent fail - not critical
          });
        } else {
          data.profile.mmr.createdAt = mmrCreatedAt;
        }

        // Convert optional Date fields safely
        if (data.profile.mmr.lastGameDate) {
          data.profile.mmr.lastGameDate = toDate(data.profile.mmr.lastGameDate);
        }
        if (data.profile.lastUsernameChange) {
          data.profile.lastUsernameChange = toDate(data.profile.lastUsernameChange);
        }
        if (data.profile.lastAvatarChange) {
          data.profile.lastAvatarChange = toDate(data.profile.lastAvatarChange);
        }

        // Migrate: assign friendTag if missing
        if (!data.profile.friendTag) {
          data.profile.friendTag = this.generateFriendTag();
          this.updateUserProfile(userSnap.id, data.profile).catch(() => {});
        }

        // Convert dates in placement history
        if (data.profile.mmr.placementHistory && Array.isArray(data.profile.mmr.placementHistory)) {
          data.profile.mmr.placementHistory = data.profile.mmr.placementHistory.map(game => ({
            ...game,
            gameDate: toDate(game.gameDate) || new Date()
          }));
        }

        return data;
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Sanitize profile to ensure all dates are valid before saving
   * Also removes undefined fields which Firebase doesn't accept
   */
  private static sanitizeProfile(profile: UserProfile): any {
    const sanitized = { ...profile };

    // Helper to validate and fix dates
    const ensureValidDate = (date: any): Date | undefined => {
      if (!date) return undefined;
      try {
        const d = date instanceof Date ? date : new Date(date);
        // If invalid date, return undefined instead
        if (isNaN(d.getTime())) return undefined;
        return d;
      } catch {
        return undefined;
      }
    };

    // Sanitize all date fields
    sanitized.lastActive = ensureValidDate(sanitized.lastActive) || new Date();
    sanitized.mmr.createdAt = ensureValidDate(sanitized.mmr.createdAt) || new Date();

    // Handle optional date fields - delete if undefined
    const lastGameDate = ensureValidDate(sanitized.mmr.lastGameDate);
    if (lastGameDate) {
      sanitized.mmr.lastGameDate = lastGameDate;
    } else {
      delete sanitized.mmr.lastGameDate;
    }

    const lastUsernameChange = ensureValidDate(sanitized.lastUsernameChange);
    if (lastUsernameChange) {
      sanitized.lastUsernameChange = lastUsernameChange;
    } else {
      delete sanitized.lastUsernameChange;
    }

    const lastAvatarChange = ensureValidDate(sanitized.lastAvatarChange);
    if (lastAvatarChange) {
      sanitized.lastAvatarChange = lastAvatarChange;
    } else {
      delete sanitized.lastAvatarChange;
    }

    // Sanitize placement history dates
    if (sanitized.mmr.placementHistory) {
      sanitized.mmr.placementHistory = sanitized.mmr.placementHistory.map(game => ({
        ...game,
        gameDate: ensureValidDate(game.gameDate) || new Date()
      }));
    }

    return sanitized;
  }

  /**
   * Update user profile data in Firebase
   */
  static async updateUserProfile(uid: string, profile: UserProfile): Promise<void> {
    try {
      const userRef = doc(db, this.USERS_COLLECTION, uid);

      // Sanitize profile before saving
      const sanitizedProfile = this.sanitizeProfile(profile);

      await updateDoc(userRef, {
        profile: sanitizedProfile,
        lastActive: new Date(),
        gamesPlayed: sanitizedProfile.mmr.totalGames
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Increment games played counter
   */
  static async incrementGamesPlayed(uid: string): Promise<void> {
    try {
      const userRef = doc(db, this.USERS_COLLECTION, uid);

      await updateDoc(userRef, {
        gamesPlayed: increment(1),
        lastActive: new Date()
      });

    } catch (error) {
      throw error;
    }
  }

  /**
   * Upgrade anonymous account to authenticated account
   * Called after successful account linking
   */
  static async upgradeToAuthenticatedAccount(
    uid: string,
    email: string
  ): Promise<void> {
    try {
      const userRef = doc(db, this.USERS_COLLECTION, uid);

      await updateDoc(userRef, {
        accountType: 'authenticated',
        email,
        upgradedAt: new Date(),
        lastActive: new Date(),
        // Remove deviceId as it's no longer needed
        deviceId: null
      });

    } catch (error) {
      throw error;
    }
  }

  /**
   * Check if user exists in Firebase
   */
  static async userExists(uid: string): Promise<boolean> {
    try {
      const userRef = doc(db, this.USERS_COLLECTION, uid);
      const userSnap = await getDoc(userRef);
      return userSnap.exists();
    } catch (error) {
      return false;
    }
  }

  /**
   * Create or update user data (upsert operation)
   */
  static async upsertUserData(userData: FirebaseUserData): Promise<void> {
    try {
      const userRef = doc(db, this.USERS_COLLECTION, userData.uid);
      await setDoc(userRef, userData, { merge: true });

    } catch (error) {
      throw error;
    }
  }

  /**
   * Update last active timestamp
   */
  static async updateLastActive(uid: string): Promise<void> {
    try {
      const userRef = doc(db, this.USERS_COLLECTION, uid);
      await updateDoc(userRef, {
        lastActive: new Date()
      });
    } catch (error) {
      // Don't throw - this is not critical
    }
  }

  /**
   * Add a completed puzzle ID to the user's profile
   */
  static async addCompletedPuzzleId(uid: string, puzzleId: string): Promise<void> {
    try {
      const userRef = doc(db, this.USERS_COLLECTION, uid);
      await updateDoc(userRef, {
        'profile.completedPuzzleIds': arrayUnion(puzzleId),
        lastActive: new Date()
      });
    } catch (error) {
    }
  }

  /**
   * Change username with 3-month cooldown
   */
  static async changeUsername(
    uid: string,
    newUsername: string,
    currentProfile: UserProfile
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Validate username length
      if (!newUsername || newUsername.trim().length < 3) {
        return { success: false, error: 'Username must be at least 3 characters' };
      }
      if (newUsername.trim().length > 12) {
        return { success: false, error: 'Username must be 12 characters or less' };
      }

      // Check for inappropriate content
      if (containsInappropriateContent(newUsername)) {
        return { success: false, error: 'Username contains inappropriate content' };
      }

      // Check if username is different
      if (newUsername.trim() === currentProfile.username) {
        return { success: false, error: 'New username must be different' };
      }

      // Check cooldown (90 days = 3 months)
      if (currentProfile.lastUsernameChange) {
        const daysSinceLastChange = Math.floor(
          (Date.now() - currentProfile.lastUsernameChange.getTime()) / (1000 * 60 * 60 * 24)
        );
        if (daysSinceLastChange < 90) {
          const daysRemaining = 90 - daysSinceLastChange;
          return {
            success: false,
            error: `You can change your username in ${daysRemaining} days`
          };
        }
      }

      // Update the profile
      const updatedProfile = {
        ...currentProfile,
        username: newUsername.trim(),
        lastUsernameChange: new Date()
      };

      await this.updateUserProfile(uid, updatedProfile);

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to change username. Please try again.' };
    }
  }
}